import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useConfigurationStore } from 'store/configurationsStore';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { IScoreCardsWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { CardGraph } from 'components/widgets/ProjectSummary/components/CardsSection/components/CardGraph/CardGraph';
import {
    serializeData,
    serializeOptions,
} from 'components/widgets/ProjectSummary/components/CardsSection/components/QualityCard/QualityCard.utils';
import { CHART_HEIGHT_IN_REPORT_VIEW } from 'components/widgets/ScheduleQualityTrend/ScheduleQualityTrend.constants';
import { useScheduleQualityTrendLogic } from 'components/widgets/ScheduleQualityTrend/ScheduleQualityTrend.logic';
import classes from 'components/widgets/ScheduleQualityTrend/ScheduleQualityTrend.module.scss';
import { useScheduleQualityTrendProvider } from 'components/widgets/ScheduleQualityTrend/ScheduleQualityTrend.provider';

export const ScheduleQualityTrend = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    externalComponents = null,
    setSubTitleForExport,
    localFilters,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title: propsTitle,
    setIsPayloadReady,
}: IScoreCardsWrapperProps) => {
    const { componentKey, routeId: id, endpoints, name: configTitle } = useConfigurationStore().scheduleQualityTrend;

    const { ui, api } = useScheduleQualityTrendProvider({
        widgetId,
        latestVersionId,
        localFilters,
        globalFilterData,
        compareVersionId,
        componentKey,
        contractId,
        endpoints,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: propsTitle ?? configTitle,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.getScheduleQualityTrend.payload,
        widgetEndpoint: api.getScheduleQualityTrend.endpoint,
    });

    useScheduleQualityTrendLogic({ setIsPayloadReady, api, widgetViewMode });

    return (
        <WidgetWithTitle
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                id={id}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                ]}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown key={'export'} title={propsTitle ?? configTitle} subTitle={subTitle} />
                    ),
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={propsTitle ?? configTitle}
                            shareLink={route}
                            projectId={projectId}
                            contractId={contractId}
                        />
                    ),
                ]}
            >
                <div className={classes.widgetCardsWrapper}>
                    {/*Loading State*/}
                    <OverlayWithSpinner isLoading={api.getScheduleQualityTrend.instance.isFetching} />

                    {/*No Data State*/}
                    {!api.getScheduleQualityTrend.instance.isFetching && !api.getScheduleQualityTrend.instance.data && (
                        <NoData />
                    )}

                    {/*Show Component State*/}
                    {api.getScheduleQualityTrend.instance.data && (
                        <CardGraph
                            data={api.getScheduleQualityTrend.instance.data}
                            xaxisTitle={'Version'}
                            yaxisTitle={'Quality'}
                            yAxisMaxValue={100}
                            height={CHART_HEIGHT_IN_REPORT_VIEW}
                            className={classes.chart}
                            serializeOptions={serializeOptions}
                            serializeData={serializeData}
                        />
                    )}
                </div>
            </WidgetWithTitle>
    );
};
